/**
 * Backup Dress functionality for cart
 * Shows "Add backup dress" button when 2+ eligible rental dress items are in cart
 * Manages the _backup_dress line item property
 * Also manages visibility of the backup dress info message
 * Manages the backup dress fee product (variant 56705572831580)
 */

class BackupDressManager {
  constructor() {
    this.FEE_PRODUCT_ID = 15434261561692;
    this.FEE_VARIANT_ID = 56705572831580;
    this.DISCOUNT_TITLE = 'backup dress';
    this.UNAVAILABLE_MESSAGE = 'The backup dress option is not available right now. Please contact us and we will sort it out.';
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.bindEvents());
    } else {
      this.bindEvents();
    }
  }

  bindEvents() {
    // Listen for cart updates - use setTimeout to ensure DOM is updated
    document.addEventListener('dispatch:cart-drawer:refresh', () => {
      setTimeout(() => this.updateVisibility(), 50);
    });
    document.addEventListener('on:cart:after-merge', () => {
      setTimeout(() => this.updateVisibility(), 50);
    });
    
    // Bind add backup dress buttons
    document.addEventListener('click', (e) => {
      if (e.target.closest('.backup-dress-add-btn')) {
        const btn = e.target.closest('.backup-dress-add-btn');
        const itemKey = btn.getAttribute('data-item-key');
        this.addBackupDress(itemKey, btn);
      }
    });

    // Bind remove backup dress buttons (pill remove)
    document.addEventListener('click', (e) => {
      if (e.target.closest('.backup-dress-pill__remove')) {
        const btn = e.target.closest('.backup-dress-pill__remove');
        const itemKey = btn.getAttribute('data-item-key');
        this.removeBackupDress(itemKey, btn);
      }
    });

    // Intercept removal of backup dress items from cart (capture phase fires before theme handlers)
    document.addEventListener('click', (e) => {
      // Check for direct "Remove" link click
      const removeLink = e.target.closest('.cart-item__remove');
      if (removeLink) {
        const cartItem = removeLink.closest('.cart-item');
        if (cartItem && cartItem.querySelector('.backup-dress-pill')) {
          e.preventDefault();
          e.stopPropagation();

          const quantityInput = cartItem.querySelector('.cart-item__quantity-input');
          if (quantityInput) {
            this.removeBackupDressItemFromCart(quantityInput.dataset.key, cartItem);
          }
          return;
        }
      }

      // Check for quantity-down click that would bring quantity to 0
      const quantityDown = e.target.closest('.quantity-down');
      if (quantityDown) {
        const cartItem = quantityDown.closest('.cart-item');
        if (cartItem && cartItem.querySelector('.backup-dress-pill')) {
          const quantityInput = cartItem.querySelector('.cart-item__quantity-input');
          if (quantityInput && parseInt(quantityInput.value, 10) <= 1) {
            e.preventDefault();
            e.stopPropagation();
            this.removeBackupDressItemFromCart(quantityInput.dataset.key, cartItem);
          }
        }
      }
    }, true); // capture phase to intercept before theme's bubble-phase handler

    // Initial check when cart drawer opens
    document.addEventListener('on:cart-drawer:before-open', () => {
      setTimeout(() => this.updateVisibility(), 100);
    });

    // Initial load
    this.updateVisibility();
  }

  /**
   * Find the fee product in the cart by product ID
   * @param {Array} items - Cart items array
   * @returns {object|undefined} The fee item if found
   */
  findFeeItem(items) {
    return items.find(i => i.product_id === this.FEE_PRODUCT_ID);
  }

  /**
   * Update all visibility states (buttons and info message)
   */
  async updateVisibility() {
    this.updateButtonVisibility();
    await this.updateInfoMessageVisibility();
  }

  /**
   * Update visibility of the backup dress info message
   * Hide if any item in cart has _backup_dress property
   */
  async updateInfoMessageVisibility() {
    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      
      // Check if any item has _backup_dress property
      const hasBackupDress = cart.items.some(item => 
        item.properties && item.properties._backup_dress
      );
      
      // Get all backup dress info elements
      const infoElements = document.querySelectorAll('.backup-dress-info');
      
      infoElements.forEach(el => {
        if (hasBackupDress) {
          el.style.display = 'none';
        } else {
          el.style.display = '';
        }
      });
    } catch (error) {
      console.error('BackupDressManager: Error updating info message visibility:', error);
    }
  }

  /**
   * Count eligible items (rental dresses with _rental_intent_token that are NOT already backup dresses)
   * @returns {number} Count of eligible items that can still be set as backup
   */
  countEligibleItems() {
    const eligibleWrappers = document.querySelectorAll('[data-eligible="true"]');
    return eligibleWrappers.length;
  }

  /**
   * Update visibility of "Set as backup dress" buttons
   * Only show if 2 or more eligible items exist (items that aren't already backup dresses)
   */
  updateButtonVisibility() {
    const eligibleCount = this.countEligibleItems();
    const addWrappers = document.querySelectorAll('.backup-dress-add-wrapper');
    
    addWrappers.forEach(wrapper => {
      if (eligibleCount >= 2) {
        wrapper.style.display = 'block';
      } else {
        wrapper.style.display = 'none';
      }
    });
  }

  /**
   * Add backup dress property to line item AND add fee product concurrently.
   * Only refreshes the cart drawer UI once both operations complete.
   * @param {string} itemKey - The cart item key
   * @param {HTMLElement} btn - The button element
   */
  async addBackupDress(itemKey, btn) {
    if (!itemKey) return;

    // Disable button during request
    btn.disabled = true;
    btn.style.opacity = '0.5';

    try {
      // Get current cart to find the item
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      
      // Find the item
      const item = cart.items.find(i => i.key === itemKey);
      if (!item) {
        throw new Error('Item not found in cart');
      }

      // Prepare properties - merge existing with new backup_dress property
      const properties = item.properties || {};
      properties._backup_dress = 'true';

      // Run the two cart mutations in sequence. Concurrent requests against the
      // same cart race each other and one of the two writes is silently lost.
      // 1. Update the line item with _backup_dress property
      // 2. Add the backup dress fee product
      const changeRes = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: itemKey,
          properties: properties
        })
      });
      if (!changeRes.ok) throw new Error('Failed to update cart item properties');

      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: this.FEE_VARIANT_ID,
            quantity: 1,
            properties: { _backup_dress_fee: 'true' }
          }]
        })
      });
      if (!addRes.ok) throw new Error('Failed to add backup dress fee product');

      // Guard: the price reduction comes from an automatic discount configured in
      // admin, not from the theme. If that discount is inactive the customer would
      // be charged full price for the backup, so roll both changes back instead.
      const discounted = await this.verifyBackupDiscount(item.variant_id);
      if (!discounted) {
        await this.rollbackBackupDress(item.variant_id);
        this.refreshCart();
        alert(this.UNAVAILABLE_MESSAGE);
        btn.disabled = false;
        btn.style.opacity = '1';
        return;
      }

      // Immediately hide the wrapper since this item is now a backup dress
      const wrapper = btn.closest('.backup-dress-add-wrapper');
      if (wrapper) {
        wrapper.remove();
      }

      // Immediately hide the backup dress info message
      document.querySelectorAll('.backup-dress-info').forEach(el => {
        el.style.display = 'none';
      });

      // Immediately update button visibility on remaining items
      this.updateButtonVisibility();

      // Refresh cart drawer once (both operations are complete)
      this.refreshCart();

    } catch (error) {
      console.error('Error adding backup dress:', error);
      alert('Failed to add backup dress. Please try again.');
      
      // Re-enable button on error
      btn.disabled = false;
      btn.style.opacity = '1';
    }
  }

  /**
   * Check the tagged line actually received the backup dress discount.
   * The line key changes when properties change, so the line is found by variant.
   * @param {number} variantId - Variant of the dress that was tagged
   * @returns {Promise<boolean>} True when the line carries the discount
   */
  async verifyBackupDiscount(variantId) {
    const cart = await fetch('/cart.js').then(res => res.json());
    const line = cart.items.find(i =>
      i.variant_id === variantId && i.properties && i.properties._backup_dress
    );
    if (!line) return false;
    return line.final_line_price === 0 || (line.discounts || []).some(d =>
      String(d.title).trim().toLowerCase() === this.DISCOUNT_TITLE
    );
  }

  /**
   * Undo a backup dress selection: strip the property and remove the fee product.
   * @param {number} variantId - Variant of the dress that was tagged
   */
  async rollbackBackupDress(variantId) {
    const cart = await fetch('/cart.js').then(res => res.json());
    const line = cart.items.find(i =>
      i.variant_id === variantId && i.properties && i.properties._backup_dress
    );
    if (line) {
      const properties = { ...line.properties };
      delete properties._backup_dress;
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: line.key, properties: properties })
      });
    }
    const feeItem = this.findFeeItem(cart.items);
    if (feeItem) {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: feeItem.key, quantity: 0 })
      });
    }
  }

  /**
   * Remove backup dress property from line item AND remove fee product.
   * Only refreshes the cart drawer UI once both operations complete.
   * @param {string} itemKey - The cart item key
   * @param {HTMLElement} btn - The button element
   */
  async removeBackupDress(itemKey, btn) {
    if (!itemKey) return;

    // Disable button during request
    btn.disabled = true;
    btn.style.opacity = '0.5';

    try {
      // Get current cart
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      
      // Find the item
      const item = cart.items.find(i => i.key === itemKey);
      if (!item) {
        throw new Error('Item not found in cart');
      }

      // Find the fee product
      const feeItem = this.findFeeItem(cart.items);

      // Prepare properties - remove backup_dress property
      const properties = item.properties || {};
      delete properties._backup_dress;

      // Run the two cart mutations in sequence (see addBackupDress for why).
      // 1. Update the line item to remove _backup_dress property
      // 2. Remove the fee product from cart
      const changeRes = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: itemKey,
          properties: properties
        })
      });
      if (!changeRes.ok) throw new Error('Failed to update cart item properties');

      if (feeItem) {
        const feeRes = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: feeItem.key,
            quantity: 0
          })
        });
        if (!feeRes.ok) throw new Error('Failed to remove backup dress fee product');
      }

      // Show the backup dress info message again
      document.querySelectorAll('.backup-dress-info').forEach(el => {
        el.style.display = '';
      });

      // Refresh cart drawer once (both operations are complete)
      this.refreshCart();

    } catch (error) {
      console.error('Error removing backup dress:', error);
      alert('Failed to remove backup dress. Please try again.');
      
      // Re-enable button on error
      btn.disabled = false;
      btn.style.opacity = '1';
    }
  }

  /**
   * Remove a backup dress item entirely from the cart AND remove the fee product.
   * Uses /cart/update.js to remove both in a single request.
   * Called when the user clicks "Remove" on a backup dress line item.
   * @param {string} itemKey - The cart item key of the backup dress item
   * @param {HTMLElement} cartItemEl - The cart item DOM element
   */
  async removeBackupDressItemFromCart(itemKey, cartItemEl) {
    try {
      // Add refreshing visual state
      const mergeItem = cartItemEl.closest('[data-merge-list-item]');
      if (mergeItem) {
        mergeItem.classList.add('merge-item-refreshing');
      }

      // Get current cart to find the fee product
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      const feeItem = this.findFeeItem(cart.items);

      // Build updates object: remove the backup dress item and the fee product
      const updates = {};
      updates[itemKey] = 0;
      if (feeItem) {
        updates[feeItem.key] = 0;
      }

      // Single request to remove both items
      const updateResponse = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates })
      });

      if (!updateResponse.ok) {
        throw new Error('Failed to remove items from cart');
      }

      // Show the backup dress info message again
      document.querySelectorAll('.backup-dress-info').forEach(el => {
        el.style.display = '';
      });

      // Dispatch cart change event so all cart UIs refresh (header count, cart drawer, etc.)
      document.dispatchEvent(
        new CustomEvent('on:cart:change', { bubbles: true, cancelable: false })
      );

    } catch (error) {
      console.error('Error removing backup dress item from cart:', error);
      // Fallback: reload page
      window.location.reload();
    }
  }

  /**
   * Refresh all cart UIs (drawer, cart page, header count)
   */
  refreshCart() {
    document.dispatchEvent(
      new CustomEvent('on:cart:change', { bubbles: true, cancelable: false })
    );
  }
}

// Initialize on load
if (typeof BackupDressManager !== 'undefined') {
  new BackupDressManager();
}
